/**
 * W1B — MessagePort-backed McpTransport for Contract B (worker remote-runtime)
 * plugins.
 *
 * The plugin-side React hooks (`useMcpResource`, `useMcpTool`) consume an
 * {@link McpTransport} — an abstraction over the host call. Contract A
 * (host-rendered) plugins pick up the host-injected transport via
 * `ExtensionRuntimeProvider`. Contract B plugins run in a Web Worker with
 * no shared object surface — the only channel is the `MessagePort` the
 * host transferred via `activate(port)`. This helper bridges the two
 * worlds: it exposes the {@link McpTransport} contract on the worker side
 * and serialises every call into a request/response envelope over the
 * port.
 *
 * # The protocol on the wire
 *
 * The wire shape mirrors `WorkerRemoteDomTransport` (in `host/worker/transport.ts`)
 * one-for-one — that transport is the host receiver and decides what a "well-
 * formed" message looks like. Two request shapes, two `:result`-suffixed reply
 * shapes, one abort envelope. Each call mints a fresh request id (`req-{n}`).
 *
 * ```jsonc
 * // worker → host
 * { type: "ethisys:mcp:getResource", id: "req-3", uri: "tickets://home" }
 * { type: "ethisys:mcp:invokeTool",  id: "req-4", name: "tickets:open", args: { ... } }
 *
 * // host → worker (matching id, suffixed type)
 * { type: "ethisys:mcp:getResource:result", id: "req-3", ok: true,  data: { uri, data } }
 * { type: "ethisys:mcp:invokeTool:result",  id: "req-4", ok: false, error: "..." }
 * ```
 *
 * Requests honour the optional `AbortSignal`. On abort, the transport posts
 * a `{ type: "ethisys:mcp:abort", id }` envelope so the host can cancel
 * in-flight work, then rejects the pending promise with an `AbortError`-shaped
 * `Error` so the consuming hooks see the same shape as native fetch cancellation.
 *
 * # Authoring shape
 *
 * ```ts
 * export async function activate(port: MessagePort): Promise<void> {
 *   const transport = createPortMcpTransport(port);
 *   const root = createRemoteRoot(port);
 *   root.render(
 *     <ExtensionRuntimeProvider transport={transport}>
 *       <App />
 *     </ExtensionRuntimeProvider>,
 *   );
 * }
 * ```
 *
 * `<App />` uses `useMcpResource` / `useMcpTool` as it would on the host
 * side; the transport translates each call into the port envelope.
 */
import type { McpTransport, UploadDocumentMeta, UploadDocumentResult } from "./transport";
import { McpToolError, isMcpErrorCode } from "../bridge/mcp-error";

/**
 * Options for {@link createPortMcpTransport}. Reserved for forward
 * compatibility — the public surface is empty in Phase 1.
 */
export interface CreatePortMcpTransportOptions
{
    /**
     * Override the request-id generator. Reserved for tests so they can
     * make request ids deterministic. Production callers never pass this.
     */
    requestIdFactory?: () => string;

    /**
     * Override the port's `addEventListener` / `removeEventListener` /
     * `postMessage` triple. Reserved for tests so the transport can be
     * exercised without instantiating a real MessageChannel.
     */
    portShimForTests?: PortShim;
}

/**
 * Minimal subset of the MessagePort surface the transport actually uses.
 * Exposed so tests can construct a polyfill without faking the full
 * MessagePort.
 */
export interface PortShim
{
    addEventListener(
        type: "message",
        listener: (event: { data: unknown }) => void,
    ): void;
    removeEventListener(
        type: "message",
        listener: (event: { data: unknown }) => void,
    ): void;
    postMessage(value: unknown, transfer?: Transferable[]): void;
}

// ── Wire-shape constants ────────────────────────────────────────────
//
// These four strings are the contract with `WorkerRemoteDomTransport`. Any
// rename here breaks the host receiver silently — the unknown message hits
// the host's default branch and is dropped. Keep these in sync with
// `host/worker/transport.ts`'s `handlePortMessage` switch / result-type
// strings.
const WIRE_INVOKE_TOOL_REQUEST = "ethisys:mcp:invokeTool";
const WIRE_INVOKE_TOOL_RESULT  = "ethisys:mcp:invokeTool:result";
const WIRE_GET_RESOURCE_REQUEST = "ethisys:mcp:getResource";
const WIRE_GET_RESOURCE_RESULT  = "ethisys:mcp:getResource:result";
const WIRE_UPLOAD_DOCUMENT_REQUEST = "ethisys:mcp:uploadDocument";
const WIRE_UPLOAD_DOCUMENT_RESULT  = "ethisys:mcp:uploadDocument:result";
const WIRE_ABORT = "ethisys:mcp:abort";

type McpResultType =
    | typeof WIRE_INVOKE_TOOL_RESULT
    | typeof WIRE_GET_RESOURCE_RESULT
    | typeof WIRE_UPLOAD_DOCUMENT_RESULT;

interface McpResultEnvelope
{
    type: McpResultType;
    id: string;
    ok?: boolean;
    error?: string;
    /**
     * Machine-readable failure classification. Optional: a host built before the bridge carried one
     * sends none, and those replies must still settle rather than being treated as malformed.
     */
    code?: unknown;
    data?: unknown;
}

function isMcpResultEnvelope(value: unknown): value is McpResultEnvelope
{
    if (value === null || typeof value !== "object")
    {
        return false;
    }
    const v = value as { type?: unknown; id?: unknown };
    return (v.type === WIRE_INVOKE_TOOL_RESULT
            || v.type === WIRE_GET_RESOURCE_RESULT
            || v.type === WIRE_UPLOAD_DOCUMENT_RESULT)
        && typeof v.id === "string";
}

/**
 * Construct an {@link McpTransport} backed by a MessagePort.
 *
 * @param port The host-transferred MessagePort for the worker surface.
 * @param options Reserved for forward compatibility / test injection.
 *
 * @returns A transport implementation honouring the {@link McpTransport}
 *          contract — fetch a resource, invoke a tool, observe abort
 *          signals, settle the promise on the host's reply envelope.
 */
export function createPortMcpTransport(
    port: MessagePort | PortShim,
    options: CreatePortMcpTransportOptions = {},
): McpTransport
{
    if (port === undefined || port === null)
    {
        throw new TypeError(
            "[createPortMcpTransport] `port` is required. Pass the MessagePort the host transferred via the worker's `activate(port)` entry point.",
        );
    }

    const shim: PortShim = options.portShimForTests ?? port as PortShim;
    const factory = options.requestIdFactory ?? defaultRequestIdFactory();

    type Resolver = {
        resolve: (value: unknown) => void;
        reject: (reason: unknown) => void;
    };
    const pending = new Map<string, Resolver>();

    function onMessage(event: { data: unknown }): void
    {
        if (!isMcpResultEnvelope(event.data))
        {
            return;
        }
        const reply = event.data;
        const resolver = pending.get(reply.id);
        if (resolver === undefined)
        {
            return;
        }
        pending.delete(reply.id);
        if (reply.ok === true)
        {
            // Host posts the payload under `data` (matching
            // `WorkerRemoteDomTransport.safePostMessage`). For getResource
            // calls the data is the resource body; for invokeTool calls it
            // is the tool's return value.
            resolver.resolve(reply.data);
        }
        else
        {
            // The code is VALIDATED, not trusted: an unknown string from a newer host degrades to
            // `internal` rather than reaching plugin code that cannot branch on it. A host with no
            // `code` at all also lands on `internal`, so an older host keeps working and simply
            // offers no classification.
            resolver.reject(new McpToolError(
                typeof reply.error === "string" ? reply.error : "Unknown host error",
                isMcpErrorCode(reply.code) ? reply.code : "internal",
            ));
        }
    }

    shim.addEventListener("message", onMessage);
    // MessagePort.addEventListener doesn't auto-start the port — without an
    // explicit `start()`, the message queue stays paused and the host's
    // replies never reach `onMessage`. This is a no-op on plain
    // EventTarget-shaped shims used in tests (the type guard guarantees
    // safety) but is required on real MessagePort instances.
    const realPort = port as Partial<MessagePort>;
    if (!options.portShimForTests && typeof realPort.start === "function")
    {
        realPort.start();
    }

    function send(
        requestType:
            | typeof WIRE_INVOKE_TOOL_REQUEST
            | typeof WIRE_GET_RESOURCE_REQUEST
            | typeof WIRE_UPLOAD_DOCUMENT_REQUEST,
        body: Record<string, unknown>,
        signal?: AbortSignal,
        transfer?: Transferable[],
    ): Promise<unknown>
    {
        const id = factory();
        return new Promise<unknown>((resolve, reject) =>
        {
            if (signal?.aborted === true)
            {
                reject(abortError());
                return;
            }
            pending.set(id, { resolve, reject });

            const onAbort = (): void =>
            {
                if (!pending.has(id))
                {
                    return;
                }
                pending.delete(id);
                shim.postMessage({ type: WIRE_ABORT, id });
                reject(abortError());
            };
            signal?.addEventListener("abort", onAbort, { once: true });

            // `transfer` moves the ArrayBuffer zero-copy for uploadDocument; the
            // JSON request shapes pass no transfer list and post by structured clone.
            shim.postMessage({ type: requestType, id, ...body }, transfer);
        });
    }

    return {
        async getResource<T>(uri: string, signal?: AbortSignal): Promise<{ uri: string; data: T }>
        {
            // Host sends only the resource body under `data`. Reconstruct the
            // `{ uri, data }` envelope the McpTransport contract returns —
            // `uri` is round-tripped from the request because the resolver
            // only sees the reply.
            const data = await send(WIRE_GET_RESOURCE_REQUEST, { uri }, signal);
            return { uri, data: data as T };
        },
        async invokeTool<TReq, TRes>(name: string, args: TReq, signal?: AbortSignal): Promise<TRes>
        {
            const data = await send(WIRE_INVOKE_TOOL_REQUEST, { name, args: args as unknown }, signal);
            return data as TRes;
        },
        async uploadDocument(
            meta: UploadDocumentMeta,
            buffer: ArrayBuffer,
            signal?: AbortSignal,
        ): Promise<UploadDocumentResult>
        {
            // The ArrayBuffer rides as a transferable — after posting, `buffer`
            // is detached on the worker side (zero-copy hand-off to the host).
            const data = await send(
                WIRE_UPLOAD_DOCUMENT_REQUEST,
                { meta, buffer },
                signal,
                [buffer],
            );
            return data as UploadDocumentResult;
        },
    };
}

function defaultRequestIdFactory(): () => string
{
    let counter = 0;
    return (): string =>
    {
        counter += 1;
        return `req-${counter}`;
    };
}

function abortError(): Error
{
    const e = new Error("Aborted");
    e.name = "AbortError";
    return e;
}
